import * as React from "react";
import {
  Button,
  Checkbox,
  Divider,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import { SearchRegular, DismissRegular } from "@fluentui/react-icons";
import { List } from "react-window";
import { FilterChipWrapper } from "./FilterChipWrapper";

const ALL_SENTINEL = "__ALL__";
const ROW_HEIGHT = 32;
const VISIBLE_ROWS = 10;

const useStyles = makeStyles({
  searchBox: {
    flex: "1",
    minWidth: "0",
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  selectedChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginBottom: "8px",
    marginTop: "8px",
  },
  chip: {
    borderRadius: "12px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "4px",
    fontSize: "12px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    paddingLeft: "4px",
    paddingRight: "4px",
  },
});

/** Option shape: key (React key), value (DB column value), text (display). */
export interface FilterOptionItem {
  key: string;
  value: string;
  text: string;
}

export interface VirtualizedFilterProps {
  label: string;
  options: FilterOptionItem[];
  /** Currently applied DB values. */
  value: string[];
  maxLabelLength?: number;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  /** Called when the user clicks the reset icon */
  onReset?: () => void;
  /** Called with selected DB values on apply. */
  onApply: (values: string[]) => void;
}

/** Custom props passed to each row via rowProps */
interface RowData {
  filtered: FilterOptionItem[];
  draft: Set<string>;
  toggle: (val: string) => void;
  rowClassName: string;
}

/**
 * Row component for react-window v2 List.
 */
function VirtualRow(
  props: {
    index: number;
    style: React.CSSProperties;
    ariaAttributes: Record<string, unknown>;
  } & RowData
): React.ReactElement | null {
  const { index, style, filtered, draft, toggle, rowClassName } = props;
  const item = filtered[index];
  if (!item) return null;
  return (
    <div style={style} className={rowClassName}>
      <Checkbox
        label={item.text}
        checked={draft.has(item.value)}
        onChange={() => toggle(item.value)}
      />
    </div>
  );
}

/**
 * Virtualized filter — for cardinality 200–5000.
 *
 * Tracks **value** (DB column value) internally and returns value[] via onApply.
 * Uses **key** for React keys. Displays **text** as the human-readable label.
 * Client-side search bar + react-window v2 virtual scroll.
 */
export const VirtualizedFilter: React.FC<VirtualizedFilterProps> = ({
  label,
  options,
  value = [],
  maxLabelLength = 50,
  isModified = false,
  onReset,
  onApply,
}) => {
  const styles = useStyles();
  const [draft, setDraft] = React.useState<Set<string>>(new Set(value));
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    setDraft(new Set(value));
  }, [value]);

  const allValues = React.useMemo(() => options.map((o) => o.value), [options]);

  /** Lookup map: value → text for display purposes. */
  const textByValue = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.value, o.text);
    return map;
  }, [options]);

  const filtered = React.useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.text.toLowerCase().includes(q));
  }, [options, search]);

  const isAllSelected =
    draft.size === allValues.length && allValues.length > 0;

  const toggle = React.useCallback(
    (val: string) => {
      if (val === ALL_SENTINEL) {
        setDraft((prev) => {
          const allSelected =
            prev.size === allValues.length && allValues.length > 0;
          return allSelected ? new Set() : new Set(allValues);
        });
        return;
      }
      setDraft((prev) => {
        const next = new Set(prev);
        if (next.has(val)) next.delete(val);
        else next.add(val);
        return next;
      });
    },
    [allValues]
  );

  function apply() {
    const arr = Array.from(draft);
    const toApply = arr.length === allValues.length ? [] : arr;
    onApply(toApply);
    setSearch("");
  }

  function cancel() {
    setDraft(new Set(value));
    setSearch("");
  }

  function valToText(val: string): string {
    return textByValue.get(val) ?? val;
  }

  // ── Display value for chip ──────────────────────────────────────────────

  function buildDisplayValue(): string {
    if (!value.length || value.length === allValues.length) return "All";
    const displayText = value.map(valToText).join(", ");
    const full = `${label}: ${displayText}`;
    if (full.length <= maxLabelLength) return displayText;
    return `${value.length} of ${options.length}`;
  }

  const isDirty = React.useMemo(() => {
    if (draft.size !== value.length) return true;
    return value.some((v) => !draft.has(v));
  }, [draft, value]);

  const listHeight = Math.min(filtered.length, VISIBLE_ROWS) * ROW_HEIGHT;

  const rowProps: RowData = React.useMemo(
    () => ({ filtered, draft, toggle, rowClassName: styles.row }),
    [filtered, draft, toggle, styles.row]
  );

  return (
    <FilterChipWrapper
      label={label}
      displayValue={buildDisplayValue()}
      isModified={isModified}
      onReset={onReset ?? (() => {})}
      isDirty={isDirty}
      onApply={apply}
      onCancel={cancel}
      surfaceWidth="300px"
    >
      <div className={styles.topRow}>
        <Input
          className={styles.searchBox}
          contentBefore={<SearchRegular />}
          placeholder="Search..."
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          size="small"
        />
        <Checkbox
          label={`All (${options.length})`}
          checked={isAllSelected}
          onChange={() => toggle(ALL_SENTINEL)}
        />
      </div>

      {/* Selected items as removable chips — show display text */}
      {draft.size > 0 && draft.size < allValues.length && (
        <div className={styles.selectedChips}>
          {Array.from(draft).map((val) => (
            <Button
              key={val}
              size="small"
              appearance="outline"
              className={styles.chip}
              icon={<DismissRegular />}
              iconPosition="after"
              onClick={() => toggle(val)}
            >
              {valToText(val)}
            </Button>
          ))}
        </div>
      )}

      <Divider />

      <List<RowData>
        rowComponent={VirtualRow}
        rowProps={rowProps}
        rowCount={filtered.length}
        rowHeight={ROW_HEIGHT}
        defaultHeight={listHeight}
        style={{ height: listHeight, maxHeight: VISIBLE_ROWS * ROW_HEIGHT }}
      />
    </FilterChipWrapper>
  );
};